import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Player } from '../player/Player.ts';
import { SyncController, type SyncStateSnapshot } from '../player/SyncController.ts';
import { accessLogoutUrl, api, ApiError } from '../api/client.ts';

function fmt(s: number): string {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function RoomPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const ctrlRef = useRef<SyncController | null>(null);
  const [snap, setSnap] = useState<SyncStateSnapshot>({
    state: 'connecting',
    position: 0,
    members: 0,
    waiting: true,
    driftS: 0,
    clockOffsetMs: 0,
    videoTitle: null,
    error: null,
  });
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [scrubPos, setScrubPos] = useState<number | null>(null);

  useEffect(() => {
    if (!id) return;
    const roomId = id;
    let cancelled = false;
    let player: Player | null = null;
    let ctrl: SyncController | null = null;

    async function setup() {
      try {
        const room = await api.getRoom(roomId);
        if (cancelled) return;
        if (!videoRef.current) return;
        player = new Player(videoRef.current);
        playerRef.current = player;
        // hook duration once available
        videoRef.current.addEventListener('loadedmetadata', () => {
          if (videoRef.current) setDuration(videoRef.current.duration);
        });

        const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        ctrl = new SyncController(player, {
          wsUrl: `${wsProtocol}://${window.location.host}/ws/room/${roomId}`,
          videoTitle: room.video_title,
          onState: (s) => setSnap(s),
        });
        ctrlRef.current = ctrl;
        ctrl.start();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          window.location.assign(accessLogoutUrl());
          return;
        }
        setSnap((s) => ({ ...s, error: (err as Error).message }));
      }
    }
    void setup();

    return () => {
      cancelled = true;
      ctrl?.stop();
      player?.destroy();
      ctrlRef.current = null;
      playerRef.current = null;
    };
  }, [id, navigate]);

  function togglePlay() {
    if (snap.state === 'playing') ctrlRef.current?.pause();
    else ctrlRef.current?.play();
  }

  function onScrubCommit(target: number) {
    // Keep the scrubber thumb pinned at the user's chosen position until the
    // server-acknowledged position update catches up. Clearing scrubPos
    // immediately would snap the thumb back to the old position while we wait
    // ~700 ms for the seek to round-trip.
    setScrubPos(target);
    ctrlRef.current?.seek(target);
  }

  useEffect(() => {
    if (scrubPos == null) return;
    // Once the live position catches up to where the user dropped the thumb,
    // hand control back to the live position.
    if (Math.abs(snap.position - scrubPos) < 0.5) setScrubPos(null);
  }, [snap.position, scrubPos]);

  function onVolume(v: number) {
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
  }

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (videoRef.current?.parentElement) {
      void videoRef.current.parentElement.requestFullscreen();
    }
  }

  async function onLeave() {
    navigate('/dashboard');
  }

  const displayPos = scrubPos ?? snap.position;

  return (
    <div className="min-h-screen p-4 flex flex-col gap-3 max-w-5xl mx-auto">
      <header className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => void onLeave()}
          className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded"
        >
          ← Leave room
        </button>
        <div className="text-zinc-400">
          {snap.videoTitle ?? 'video'} · {snap.members} viewer{snap.members === 1 ? '' : 's'}
        </div>
      </header>

      <div className="relative bg-black rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          className="w-full aspect-video bg-black"
          playsInline
          // hide native controls; ours sync with the room.
          controls={false}
          aria-label="Synchronized video"
        />
        {snap.waiting && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-sm text-zinc-300 pointer-events-none">
            syncing…
          </div>
        )}
        {snap.error && (
          <div className="absolute top-2 left-2 right-2 px-3 py-2 bg-red-900/80 text-sm rounded">
            {snap.error}
          </div>
        )}
      </div>

      {/* Transport bar */}
      <div className="flex items-center gap-3 bg-zinc-900 rounded px-3 py-2">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={snap.state === 'playing' ? 'Pause' : 'Play'}
          className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center"
        >
          {snap.state === 'playing' ? '❚❚' : '▶'}
        </button>

        <span className="text-xs text-zinc-400 tabular-nums">{fmt(displayPos)}</span>
        <input
          aria-label="Scrubber"
          type="range"
          min={0}
          max={Math.max(1, duration)}
          step={0.1}
          value={scrubPos ?? snap.position}
          onChange={(e) => setScrubPos(Number.parseFloat(e.target.value))}
          onMouseUp={(e) => onScrubCommit(Number.parseFloat((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => onScrubCommit(Number.parseFloat((e.target as HTMLInputElement).value))}
          className="flex-1"
        />
        <span className="text-xs text-zinc-400 tabular-nums">{fmt(duration)}</span>

        <label className="flex items-center gap-2 text-xs text-zinc-400">
          <span aria-hidden="true">🔊</span>
          <input
            aria-label="Volume"
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => onVolume(Number.parseFloat(e.target.value))}
            className="w-20"
          />
        </label>

        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label="Fullscreen"
          className="px-2 py-1 text-sm bg-zinc-800 hover:bg-zinc-700 rounded"
        >
          ⛶
        </button>
      </div>

      <div className="text-xs text-zinc-500 flex gap-4 tabular-nums">
        <span>state: {snap.state}</span>
        <span>drift: {snap.driftS.toFixed(3)}s</span>
        <span>clock offset: {snap.clockOffsetMs.toFixed(0)}ms</span>
      </div>
    </div>
  );
}
